/* jshint esnext: true */
import { $document, APP_NAME, $html, $body } from '../utils/environment';
import AbstractModule from './AbstractModule';

import Scrollbar from 'smooth-scrollbar';

const MODULE_NAME = 'Form';
const EVENT_NAMESPACE = `${APP_NAME}.${MODULE_NAME}`;

const EVENT = {
    CHANGE: `change.${EVENT_NAMESPACE}`,
    CLICK:  `click.${EVENT_NAMESPACE}`,
    INPUT:  `input.${EVENT_NAMESPACE}`,
    SUBMIT: `submit.${EVENT_NAMESPACE}`,
    OPEN:   `open.Form`
};

const SELECTOR = {
    FORM:         '.js-form',
    INPUT:        '.js-input',
    INPUTPARENT:  '.js-input-parent',
    INPUTMESSAGE: '.js-input-message'
};

const CLASS = {
    ERROR:    'has-error',
    NOTEMPTY: 'is-not-empty',
    OPEN:     'is-open'
};

export default class extends AbstractModule {
    constructor(options) {
        super(options);

        this.isTransmitting     = false;
        this.contentType        = 'application/x-www-form-urlencoded; charset=UTF-8';
        this.processData        = true;
        this.usingFormdata      = false;
        this.hasScrollbar       = $html.hasClass('has-smooth-scroll');
        this.captchaId          = null;
    }

    init() {
        this.$form       = $(SELECTOR.FORM,         this.$el);
        this.$inputs     = $(SELECTOR.INPUT,        this.$el);
        this.$messages   = $(SELECTOR.INPUTMESSAGE, this.$el);
        this.$fieldsets  = $('.js-fieldset',        this.$el);
        this.$preSubmit  = $('.js-pre-submit',      this.$el);
        this.$postSubmit = $('.js-post-submit',     this.$el);
        this.$captcha    = $('.js-captcha',         this.$el);
        this.$selects    = $('.js-select',          this.$el);

        // Use FormData only if file inputs are found
        if (this.$inputs.filter('[type="file"]').length > 0) {
            this.contentType   = false;
            this.processData   = false;
            this.usingFormdata = true;
        }

        this.$selects.selectric();

        if (this.hasScrollbar) {
            this.scrollbar = Scrollbar.init($('.js-container-scroll', this.$el)[0]);
        }

        this.$el.on(EVENT.OPEN,                   (event) => this.open(event)    );
        this.$el.on(EVENT.CLICK,  '.js-close',    (event) => this.close(event)   );
        this.$el.on(EVENT.INPUT,  SELECTOR.INPUT, (event) => this.onInput(event) );
        this.$el.on(EVENT.CHANGE, SELECTOR.INPUT, (event) => this.onInput(event) );
        this.$el.on(EVENT.SUBMIT, SELECTOR.FORM,  (event) => this.onSubmit(event));
    }

    /**
     * Open the form popover and pause the product slider.
     *
     * @return {void}
     */
    open() {
        this.$el.addClass(CLASS.OPEN);

        $document.triggerHandler({
            type: `pause.ProductManager`
        });
    }

    /**
     * Close the form popover.
     *
     * @return {void}
     */
    close() {
        this.$el.removeClass(CLASS.OPEN);
    }

    /**
     * Update label state on field update.
     *
     * @param  {Object} event Change event
     * @return {void}
     */
    onInput(event) {
        const $el     = $(event.currentTarget);
        const $parent = $el.parents(SELECTOR.INPUTPARENT);

        $parent.removeClass(CLASS.ERROR);

        if ($el.val().length > 0) {
            $el.addClass(CLASS.NOTEMPTY);
        } else {
            $el.removeClass(CLASS.NOTEMPTY);
            if ($el.hasClass('is-required')) {
                $parent.addClass(CLASS.ERROR);
            }
        }

        if (event.currentTarget.type === 'file') {
            this.changeFileInputLabel($el);
        }
    }

    /**
     * Set the filename in the file input
     *
     * @param  {Array} $el jQuery node
     * @return {void}
     */
    changeFileInputLabel($el) {
        const filename = $el[0].files[0].name;

        if ($el.next().hasClass('js-file-name')) {
            $el.next().text(filename)
        }
    }

    /**
     * Submits the form data to an URL with $.ajax.
     *
     * @param  {Object} Submit event
     * @return {void}
     */
    onSubmit(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.captchaId === null) {
            const id = this.$captcha.html('').get(0).getAttribute('id');
            this.captchaId = window.grecaptcha.render(id, {
                sitekey: window.recaptchaKey,
                size: 'invisible',
                callback: (token) => {
                    $('.js-captcha-response', this.$form).val(token);
                    this.$el.addClass('has-loaded-captcha');
                    this.sendData();
                }
            });
            window.grecaptcha.execute(this.captchaId);
        } else {
            this.sendData();
        }
    }

    sendData() {
        if (!this.isTransmitting) {
            this.toggleLoading();

            $(`.${CLASS.ERROR}`, this.$form).removeClass(CLASS.ERROR);

            if (this.$messages.length > 0) {
                this.$messages.prop('aria-hidden', 'true').text('');
            }

            const data = (this.usingFormdata) ? new FormData(this.$form.get(0)) : this.$form.serialize();

            setTimeout(() => {
                $.ajax({
                    url:         (this.$form.prop('action')),
                    method:      (this.$form.prop('method') || 'POST'),
                    contentType: this.contentType,
                    processData: this.processData,
                    dataType:    'json',
                    data:        data
                })
                .done((response, textStatus) => {
                    if (response.success === true) {
                        this.$preSubmit.fadeOut(() => {
                            this.$form.trigger('reset');
                            this.$postSubmit.fadeIn();
                        });

                        if (this.hasScrollbar) {
                            this.scrollbar.scrollTo(0, 0, 600);
                            this.scrollbar.update();
                        }

                        setTimeout(() => {
                            this.close();
                        },2000);
                    } else {
                        console.warn(`Something went wrong with the request (done, ${textStatus})`);
                    }
                })
                .fail((response, textStatus) => {
                    if (typeof response.responseJSON.errors !== 'undefined') {
                        this.manageErrors(response.responseJSON.errors);
                    } else {
                        console.group(`Something went wrong with the request (fail, ${textStatus})`);
                        console.log(response);
                        console.log(textStatus);
                        console.groupEnd();
                    }
                })
                .always(() => {
                    setTimeout(() => {
                        this.toggleLoading();
                    }, 600);
                });
            }, 600);
        }
    }

    /**
     * Simple method for toggling animation states.
     *
     * @return void
     */
    toggleLoading() {
        this.isTransmitting = !this.isTransmitting;
        this.$el.toggleClass('is-form-state-loading');
    }

    /**
     * Manage errors sent by back end on front end and reset captcha.
     *
     * @param  {array}  errors  List of error objects
     * @return void
     */
    manageErrors(errors) {
        let i = 0;
        const len = errors.length;

        for (; i < len; i++) {
            const $input = $(`[name="${errors[i].property}"]`, this.$form);
            const $parent = $input.parents(SELECTOR.INPUTPARENT).eq(0);

            if (i === 0) {
                this.scrollbar.scrollTo(
                    0,
                    $input.offset().top + this.scrollbar.scrollTop + (window.innerHeight / -2),
                    600
                );
                this.scrollbar.update();
            }

            $parent.addClass(CLASS.ERROR);
            $parent.find(SELECTOR.INPUTMESSAGE).prop('aria-hidden', 'false').text(errors[i].message);
        }

        if (this.captchaId !== null) {
            window.grecaptcha.reset(this.captchaId);
        }
    }

    destroy() {
        if (this.captchaId !== null) {
            window.grecaptcha.reset(this.captchaId);
        }
        this.$selects.selectric('destroy');
        this.$el.off(`.${EVENT_NAMESPACE}`);

        if (this.hasScrollbar) {
            this.scrollbar.destroy();
        }
    }
}
